import React, { useState, useRef } from 'react';
import { Modal, Button, Form, ProgressBar, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudUploadAlt, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useFileManagerContext, SET_FILE_MANAGER_STATE } from '../../../providers/FileManager';
import FileSystemService from '../../../service/FileSystemService';

interface FileUploadModalProps {
  onClose: () => void;
  currentPath?: string;
}

interface FileProgress {
  [key: number]: number;
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({ onClose, currentPath = '/' }) => {
  const { fileManagerDispatch } = useFileManagerContext();
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<FileProgress>({});
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      setFiles(prevFiles => [...prevFiles, ...selectedFiles]);
    }
  };

  // Handle file drag over
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Handle file drop
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      setFiles(prevFiles => [...prevFiles, ...droppedFiles]);
    }
  };

  // Handle remove file from list
  const handleRemoveFile = (index: number) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  // Handle upload
  const handleUpload = async () => {
    if (files.length === 0) return;
    
    setIsUploading(true);
    setError(null);
    
    // Initialize progress for each file
    const initialProgress: FileProgress = {};
    files.forEach((_, index) => {
      initialProgress[index] = 0;
    });
    setUploadProgress(initialProgress);
    
    // Track if any upload failed
    let hasErrors = false;
    
    // Upload each file
    for (let i = 0; i < files.length; i++) {
      try {
        // In a real app, you would track actual upload progress
        // For demonstration, we'll simulate progress
        for (let progress = 0; progress <= 100; progress += 10) {
          setUploadProgress(prev => ({
            ...prev,
            [i]: progress
          }));
          
          // Add a small delay to simulate uploading
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        // Upload the file
        await FileSystemService.uploadFile(currentPath, files[i]);
        
        // Set final progress
        setUploadProgress(prev => ({
          ...prev,
          [i]: 100
        }));
      } catch (err) {
        console.error(`Error uploading file ${files[i].name}:`, err);
        setError(`Failed to upload ${files[i].name}: ${err instanceof Error ? err.message : String(err)}`);
        hasErrors = true;
        
        // Mark this file's progress as failed
        setUploadProgress(prev => ({
          ...prev,
          [i]: -1
        }));
      }
    }
    
    // If no errors, close after a brief delay
    if (!hasErrors) {
      setTimeout(() => {
        onClose();
        
        // Refresh the file manager
        fileManagerDispatch({
          type: SET_FILE_MANAGER_STATE,
          payload: { refreshTimestamp: Date.now() }
        });
      }, 1000);
    } else {
      setIsUploading(false);
    }
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Upload Files</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}
        
        {/* Drag and drop area */}
        <div 
          className="p-5 text-center border rounded mb-3 cursor-pointer"
          style={{ 
            backgroundColor: '#f8f9fa',
            borderStyle: 'dashed'
          }}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <FontAwesomeIcon icon={faCloudUploadAlt} size="3x" className="text-primary mb-3" />
          <h5>Drag & Drop Files Here</h5>
          <p className="text-muted">or click to browse files</p>
          <Form.Control 
            type="file" 
            multiple 
            className="d-none"
            ref={fileInputRef}
            onChange={handleFileSelect}
          />
        </div>
        
        {/* File list */}
        {files.length > 0 && (
          <div className="mb-3">
            <h6 className="mb-3">Selected Files</h6>
            <div className="list-group">
              {files.map((file, index) => (
                <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <span className="text-primary">
                        <FontAwesomeIcon icon={faCheck} className={uploadProgress[index] === 100 ? 'd-inline-block' : 'd-none'} />
                      </span>
                    </div>
                    <div>
                      <div>{file.name}</div>
                      <small className="text-muted">{(file.size / 1024).toFixed(2)} KB</small>
                    </div>
                  </div>
                  <div className="d-flex align-items-center">
                    {isUploading ? (
                      <div style={{ width: '150px' }}>
                        {uploadProgress[index] > 0 && uploadProgress[index] !== -1 ? (
                          <ProgressBar 
                            now={uploadProgress[index]} 
                            label={`${uploadProgress[index]}%`}
                            variant={uploadProgress[index] === 100 ? 'success' : 'primary'} 
                          />
                        ) : uploadProgress[index] === -1 ? (
                          <span className="text-danger">Failed</span>
                        ) : (
                          <span>Waiting...</span>
                        )}
                      </div>
                    ) : (
                      <Button 
                        variant="link" 
                        className="text-danger p-0" 
                        onClick={() => handleRemoveFile(index)}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleUpload} 
          disabled={files.length === 0 || isUploading}
        >
          {isUploading ? 'Uploading...' : 'Upload'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FileUploadModal;